/* The board: jobs across admin-configured stages.
 *
 * Nothing here assumes a stage count or a stage name — the columns are whatever
 * the admin list says. Two modes:
 *
 *  - **Fit** — the default. Every stage on the screen at once, columns sharing
 *    the width and the card set in smaller type to suit. The first question the
 *    board answers is "what's in the shop", and that wants one look, not a
 *    sideways scroll. Where the screen cannot give every stage a readable
 *    column — an iPad, mostly — fit steps down to the snug card rather than
 *    scroll sideways: smaller type, tighter boxes, the same facts. Below the
 *    snug floor it does scroll, because shrinking past legibility is worse.
 *    A snug column is too narrow for the whole card, so its cards are drawn
 *    short — no date in, no labels over the values, overdue said with the
 *    card's own ground. Pressing a stage header opens that one column to a
 *    width the whole card reads at and puts every one of those facts back;
 *    pressing it again gives the width to the rest of the board. One at a time:
 *    two open columns is two half-open ones.
 *  - **Full board** — every stage at the card's designed width, scrolling
 *    sideways. This is the mode for working a stage rather than reading the
 *    shop: the same card with room to breathe. It has two scales of its own.
 *    A screen that cannot hold three designed-width columns — a tablet — gets
 *    `full-tight`: the same card, the same layout, type and padding a step
 *    down, in a column narrow enough that a third one arrives. The card is
 *    what the shop knows either way; what changes is how much of the board it
 *    is standing next to.
 *
 * Both show every stage the admin has defined — a scale, never a filter — and
 * both show the whole card wherever the column can hold it, because a board you
 * have to open a job to read is a board you stop trusting. The one place that
 * gives is a snug column, where the whole card is a card too tall to see two
 * of; there the shop reads the short card and opens the column it is working.
 * The stage count is read once, to work out how much room there is to share; no
 * stage name is read at all, and neither picks what gets drawn.
 *
 * Moving a job is an edit, so it goes through the same assignment gate as any
 * other write. Tasks never appear here — only jobs move. */
import type { CSSProperties, ReactNode } from 'react';
import { areaFor } from '@/domain/items';
import { useApp } from '@/store/useApp';
import { PageHead, Seg } from '@/ui/controls';
import { useNarrowerThan, usePageGutter, withGutter } from '@/ui/viewport';
import { JobCard, type CardSize } from './JobCard';

/* What a fit column needs before the card starts truncating: the date box at
   the fit scale, plus the padding around it. The snug floor is the same sum a
   scale down. Both are floors, not widths — the columns share whatever the
   screen has and only fall back on these when it runs out. */
const FIT_COLUMN = 132;
const SNUG_COLUMN = 112;
/* What an opened column is worth: enough for the snug card to put its two dates
   side by side and its four spec facts in more than one column, so the detail
   comes back as width rather than as height. A card in here is about as tall as
   the short one beside it — which is the point of opening it. */
const OPEN_COLUMN = 236;
const COLUMN_GAP = 8;

/* The full board's column: the card at its designed width, and the same card a
   step down for a screen that cannot show three of them. Both scroll sideways —
   this mode is for working a stage — but two and a bit columns is not a board
   anybody can work an iPad on, so below three designed columns the card, the
   column and the gaps all step down together and a third column arrives.
   Arithmetic, not a device: three columns plus their gaps plus the board's own
   padding is the width, and it moves if any of those do. */
const FULL_COLUMN = 392;
const FULL_TIGHT_COLUMN = 296;
const FULL_GAP = 16;
const FULL_PAD = 48;
const FULL_TIGHT_AT = FULL_COLUMN * 3 + FULL_GAP * 2 + FULL_PAD;

/* What the column around the cards is worth at each card scale. One table
   beside `JobCard`'s `SCALE`, for the same reason: a column that stepped down
   while its header didn't is a header wearing more of the screen than the cards
   it names. Keyed by the card the column holds, so the two cannot disagree.

   The board's own top and bottom are here; its two sides are not. At fit and
   snug the sides are the page's gutter (`usePageGutter`), so the first column
   stands under the word Board, and at those scales `boardEnd` is not read. On
   the full board they are `boardEnd`, which is the padding that card scale is
   drawn with. Whichever it is, only the left of it is padding: the right is a
   spacer item at the end of the row, because a scroll container's end padding
   is not part of what it scrolls and the board scrolls sideways whenever the
   stages want more width than the screen has. The gap before that spacer is
   half of the distance, which is why it is drawn a gap narrower. */
const COLUMN = {
  full: {
    boardTop: 6,
    boardBottom: 24,
    boardEnd: 24,
    boardGap: FULL_GAP,
    pad: 12,
    radius: 18,
    cardGap: 12,
    headPad: '7px 10px',
    headGap: 8,
    headMargin: '-4px -4px 12px',
    headRadius: 12,
    headWrap: 'nowrap',
    name: 14,
    tracking: '0.1em',
    count: 12,
    wip: 9,
    wipPad: '2px 7px',
    wipTracking: '0.09em',
    emptyPad: '14px 10px',
    empty: 13,
  },
  'full-tight': {
    boardTop: 6,
    boardBottom: 20,
    boardEnd: 14,
    boardGap: 10,
    pad: 9,
    radius: 15,
    cardGap: 8,
    headPad: '6px 9px',
    headGap: 7,
    headMargin: '-3px -3px 9px',
    headRadius: 11,
    headWrap: 'nowrap',
    name: 12,
    tracking: '0.09em',
    count: 12,
    wip: 9,
    wipPad: '2px 6px',
    wipTracking: '0.09em',
    emptyPad: '11px 9px',
    empty: 12,
  },
  fit: {
    boardTop: 6,
    boardBottom: 20,
    boardEnd: 16,
    boardGap: COLUMN_GAP,
    pad: 8,
    radius: 14,
    cardGap: 8,
    headPad: '6px 8px',
    headGap: 6,
    headMargin: '-2px -2px 8px',
    headRadius: 10,
    headWrap: 'wrap',
    name: 11,
    tracking: '0.06em',
    count: 12,
    wip: 9,
    wipPad: '2px 7px',
    wipTracking: '0.09em',
    emptyPad: '10px 8px',
    empty: 12,
  },
  snug: {
    boardTop: 6,
    boardBottom: 20,
    boardEnd: 16,
    boardGap: COLUMN_GAP,
    pad: 6,
    radius: 14,
    cardGap: 6,
    headPad: '5px 6px',
    headGap: 5,
    headMargin: '-2px -2px 8px',
    headRadius: 10,
    headWrap: 'wrap',
    name: 10,
    tracking: '0.06em',
    count: 11,
    wip: 8.5,
    wipPad: '2px 5px',
    wipTracking: '0.06em',
    emptyPad: '8px 7px',
    empty: 11,
  },
} as const satisfies Record<CardSize, unknown>;

/** A stage column's header. It is a heading on a board whose cards are already
 *  whole, and a button on one whose cards are short — pressing it is what
 *  trades the rest of the board's width for the whole card in this column. Same
 *  row either way: the tone dot, the stage name, the count and the WIP pill do
 *  not move when the thing around them becomes pressable. */
function ColumnHead({
  as,
  open,
  name,
  onClick,
  style,
  children,
}: {
  as: 'button' | 'div';
  open: boolean;
  name: string;
  onClick: () => void;
  style: CSSProperties;
  children: ReactNode;
}) {
  if (as === 'div') return <div style={style}>{children}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      title={open ? `Show ${name} short again` : `Show every detail in ${name}`}
      style={{
        ...style,
        border: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function BoardScreen() {
  const gutter = usePageGutter();
  const jobs = useApp((s) => s.jobs);
  const jobsError = useApp((s) => s.jobsError);
  const stages = useApp((s) => s.stages);
  const boardMode = useApp((s) => s.boardMode);
  const boardOpen = useApp((s) => s.boardOpen);
  const go = useApp((s) => s.go);
  const set = useApp((s) => s.set);
  const openNewJob = useApp((s) => s.openNewJob);
  const dropOnColumn = useApp((s) => s.dropOnColumn);

  const fit = boardMode === 'fit';
  // Fit is the condensed layout; the full board is the one shaped differently,
  // so most of what follows asks `condensed`, not the mode.
  const condensed = fit;
  // The breakpoint is the board's own arithmetic, not a device: how wide the
  // screen would have to be to give every stage the shop has defined a column
  // its card reads at. Add a stage and the step down arrives earlier, which is
  // the honest answer — nothing here counts nine columns.
  const roomForFit =
    stages.length * FIT_COLUMN +
    Math.max(stages.length - 1, 0) * COLUMN_GAP +
    gutter.start +
    gutter.end;
  const snug = useNarrowerThan(roomForFit) && fit;
  // The full board's own step down, and the same kind of arithmetic: a screen
  // that cannot hold three designed-width columns gets the card, the column and
  // the gaps a size smaller, which is what puts the third column on an iPad.
  const tightFull = useNarrowerThan(FULL_TIGHT_AT) && !fit;
  // Opening a column only means anything where the column is too narrow for the
  // whole card. Everywhere else the card is already whole, and a control that
  // does nothing is worse than no control.
  const openId = snug ? boardOpen : null;
  // Both modes draw every stage the admin has defined — a scale, never a filter.
  const shownStages = stages;
  // One name for how big everything is drawn: the card, and the column around
  // it. Nothing below asks the mode or the breakpoint again.
  const size: CardSize = fit ? (snug ? 'snug' : 'fit') : tightFull ? 'full-tight' : 'full';
  const col = COLUMN[size];
  /* What the board is padded by down each side. At fit and snug that is the
     page's own gutter, the same one the title band above takes, so the first
     column stands on the same line as the word Board. The full board keeps the
     padding its card scale is drawn with — it is a card at a designed width in
     a board that scrolls, not a page laid out to the screen. Half of it is
     padding on the scroller and half is the spacer that ends the row, because
     a scroll container's end padding is not part of what it scrolls. */
  const boardStart = condensed ? gutter.start : col.boardEnd;
  const boardStop = condensed ? gutter.end : col.boardEnd;
  const endSpacer = Math.max(0, boardStop - col.boardGap);
  const at = (id: string) => stages.find((s) => s.id === id);

  const shopJobs = jobs.filter((j) => !at(j.stage)?.terminal);

  const summary = `${shopJobs.length} jobs in the shop · ${shopJobs.reduce(
    (a, j) => a + j.items,
    0,
  )} items · ${shopJobs.reduce((a, j) => a + areaFor(j), 0).toFixed(0)} m² · Mon 31 Aug`;

  return (
    <section style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* The one row every destination opens with. The shop's count used to sit
          under the title as the 1440px artboard has it, and it wore about 30px
          the columns could have had — on a board that is one screen or it is
          nothing, that is the trade to make. It is beside the title on every
          width now, the same as the other four screens; the actions are in the
          same row, so they rise with it.

          A board that could not be read must say so, which is what the caption
          carries when the jobs never arrived: empty columns and a board the
          shop's work never reached look identical, and the second one is the
          one somebody needs to act on.

          The wrapper is the page gutter, which every other screen's <section>
          supplies and this one cannot: the columns below are flush to the edge
          and pad themselves by card scale. At fit and snug they pad themselves by
          this same gutter, so the first column stands under the word Board. */}
      <div style={{ flex: 'none', padding: withGutter(gutter, 0) }}>
        <PageHead title="Board" caption={jobsError || summary} urgent={!!jobsError}>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => go('jobs')}
              style={{ height: 40 }}
            >
              Search all jobs
            </button>
            <Seg
              options={[
                { label: 'Fit', on: fit, pick: () => set('boardMode', 'fit') },
                {
                  label: 'Full board',
                  on: boardMode === 'full',
                  pick: () => set('boardMode', 'full'),
                },
              ]}
            />
            <button
              type="button"
              className="btn btn-primary blueprint"
              onClick={() => openNewJob()}
              style={{ height: 40, paddingInline: 16 }}
            >
              New job
            </button>
          </div>
        </PageHead>
      </div>

      {/* The shell holds the viewport, so this is a real height and the
          sideways scrollbar sits on screen rather than below the fold. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: `${col.boardTop}px 0 ${col.boardBottom}px ${boardStart}px`,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: col.boardGap,
            alignItems: 'stretch',
            height: '100%',
            minWidth: condensed ? 0 : 'max-content',
            width: condensed ? '100%' : undefined,
          }}
        >
          {shownStages.map((stage) => {
            const list = jobs.filter((j) => j.stage === stage.id);
            const over = stage.wip > 0 && list.length > stage.wip;
            const tone = stage.tone;
            const open = openId === stage.id;

            return (
              <div
                key={stage.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  dropOnColumn(stage.id);
                }}
                style={{
                  // Fit shares the width evenly, but never below the point its
                  // card stops being readable — on a screen too narrow even for
                  // the snug card it scrolls rather than lie. The card answers a
                  // narrow column by stacking its two grids, so each floor is
                  // where that scale's date and fact stop fitting on one line.
                  ...(condensed
                    ? open
                      ? { flex: '0 0 auto', width: OPEN_COLUMN }
                      : { flex: '1 1 0', minWidth: snug ? SNUG_COLUMN : FIT_COLUMN }
                    : {
                        width: tightFull ? FULL_TIGHT_COLUMN : FULL_COLUMN,
                        flex: 'none',
                      }),
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: '100%',
                  border: `1px solid ${
                    tone ? `color-mix(in srgb, ${tone} 46%, var(--line))` : 'var(--line)'
                  }`,
                  borderRadius: col.radius,
                  padding: col.pad,
                  background: tone
                    ? `color-mix(in srgb, ${tone} 22%, var(--ground))`
                    : 'var(--ground)',
                }}
              >
                {/* A narrow column wraps the header onto two lines rather than
                    truncating the stage name — the name is the one thing on
                    this row that must always be readable.

                    Where the cards are short it is also the control that opens
                    the column, so it is a real button: 40px of it, which is
                    under the shop's 48px floor and the second place in the app
                    that is — like the job sheet's fold tab, a header tall
                    enough for a glove would be taller than the cards it is
                    there to make room for. */}
                <ColumnHead
                  as={snug ? 'button' : 'div'}
                  onClick={() => set('boardOpen', open ? null : stage.id)}
                  open={open}
                  name={stage.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: col.headGap,
                    flexWrap: col.headWrap,
                    padding: col.headPad,
                    minHeight: snug ? 40 : undefined,
                    margin: col.headMargin,
                    borderRadius: col.headRadius,
                    background: tone
                      ? `color-mix(in srgb, ${tone} 40%, var(--surface))`
                      : 'var(--surface)',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: 9,
                      height: 9,
                      flex: 'none',
                      borderRadius: '50%',
                      background: tone || 'transparent',
                      border: `1px solid ${tone ? 'var(--ink)' : 'var(--dim)'}`,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      fontSize: col.name,
                      letterSpacing: col.tracking,
                      textTransform: 'uppercase',
                    }}
                  >
                    {stage.name}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: col.count,
                      color: 'var(--ink-muted)',
                      marginLeft: 'auto',
                    }}
                  >
                    {list.length}
                  </span>
                  {stage.wip > 0 ? (
                    <span
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: col.wip,
                        fontWeight: 600,
                        letterSpacing: col.wipTracking,
                        textTransform: 'uppercase',
                        padding: col.wipPad,
                        borderRadius: 999,
                        color: over ? 'var(--on-urgent)' : 'var(--ink-muted)',
                        background: over ? 'var(--urgent)' : 'var(--line-soft)',
                      }}
                    >
                      {over
                        ? `Over WIP ${list.length}/${stage.wip}`
                        : `WIP ${list.length}/${stage.wip}`}
                    </span>
                  ) : null}
                  {snug ? (
                    <span
                      aria-hidden
                      style={{ fontSize: 9, color: 'var(--ink-muted)', lineHeight: 1 }}
                    >
                      {open ? '▾' : '▸'}
                    </span>
                  ) : null}
                </ColumnHead>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: col.cardGap,
                    overflowY: 'auto',
                    padding: '2px 2px 8px',
                  }}
                >
                  {list.map((job, i) => (
                    <JobCard
                      key={job.code}
                      job={job}
                      plan={job.plan}
                      index={i}
                      count={list.length}
                      size={size}
                      terse={snug && !open}
                    />
                  ))}
                  {list.length === 0 ? (
                    <p
                      style={{
                        margin: 0,
                        padding: col.emptyPad,
                        fontSize: col.empty,
                        color: 'var(--ink-muted)',
                        border: '1px dashed var(--line)',
                        borderRadius: 12,
                        lineHeight: 1.4,
                      }}
                    >
                      {stage.def
                        ? 'Nothing waiting. New jobs land here — press New job.'
                        : `Nothing in ${stage.name.toLowerCase()}. Drag a card here when it starts.`}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}
          {/* The board's right-hand gutter, as a column of nothing. It cannot
              be padding: a scroll container's end padding is not part of what
              it scrolls, so the moment the columns are wider than the screen —
              which on an iPad is most of the time — the padding-right is
              dropped and the last column is cut off flush against the glass
              while the first still has its own. A flex item is a box, so it
              survives the scroll. Its width is the gutter less the gap it
              already gets from the column before it, and it takes the same
              width from the row whether the board scrolls or shares. */}
          <div aria-hidden style={{ flex: `0 0 ${endSpacer}px` }} />
        </div>
      </div>
    </section>
  );
}
